import { useCallback, useEffect, useRef, MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent } from 'react';

const DRAG_THRESHOLD_PX = 5;
const AUTO_SCROLL_SPEED_PX_PER_SEC = 30;

interface UseDragScrollOptions {
  autoScroll?: boolean;
}

export function useDragScroll<T extends HTMLElement = HTMLDivElement>(options: UseDragScrollOptions = {}) {
  const { autoScroll = false } = options;

  const ref = useRef<T>(null);
  const isPointerDownRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const startXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const isPausedRef = useRef(false);
  const directionRef = useRef<1 | -1>(1);

  useEffect(() => {
    if (!autoScroll) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId: number;
    let lastTs: number | undefined;

    const step = (ts: number) => {
      const el = ref.current;
      const dt = lastTs !== undefined ? ts - lastTs : 0;
      lastTs = ts;

      if (el && !isPausedRef.current && !isPointerDownRef.current) {
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll > 0) {
          let next = el.scrollLeft + (directionRef.current * AUTO_SCROLL_SPEED_PX_PER_SEC * dt) / 1000;
          if (next >= maxScroll) {
            next = maxScroll;
            directionRef.current = -1;
          } else if (next <= 0) {
            next = 0;
            directionRef.current = 1;
          }
          el.scrollLeft = next;
        }
      }

      rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [autoScroll]);

  const onMouseDown = useCallback((e: ReactMouseEvent<T>) => {
    const el = ref.current;
    if (!el) return;
    e.preventDefault();
    isPointerDownRef.current = true;
    hasDraggedRef.current = false;
    startXRef.current = e.pageX - el.offsetLeft;
    startScrollLeftRef.current = el.scrollLeft;
    el.style.cursor = 'grabbing';
  }, []);

  const onMouseMove = useCallback((e: ReactMouseEvent<T>) => {
    const el = ref.current;
    if (!el || !isPointerDownRef.current) return;

    const x = e.pageX - el.offsetLeft;
    const delta = x - startXRef.current;

    if (Math.abs(delta) > DRAG_THRESHOLD_PX) {
      hasDraggedRef.current = true;
    }

    if (hasDraggedRef.current) {
      e.preventDefault();
      el.scrollLeft = startScrollLeftRef.current - delta;
    }
  }, []);

  const endDrag = useCallback(() => {
    const el = ref.current;
    isPointerDownRef.current = false;
    if (el) el.style.cursor = '';
  }, []);

  const onMouseEnter = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const onMouseLeave = useCallback(() => {
    isPausedRef.current = false;
    endDrag();
  }, [endDrag]);

  const onTouchStart = useCallback((_e: ReactTouchEvent<T>) => {
    isPausedRef.current = true;
  }, []);

  const onTouchEnd = useCallback((_e: ReactTouchEvent<T>) => {
    isPausedRef.current = false;
  }, []);

  const onClickCapture = useCallback((e: ReactMouseEvent<T>) => {
    if (hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      hasDraggedRef.current = false;
    }
  }, []);

  return {
    ref,
    onMouseDown,
    onMouseMove,
    onMouseUp: endDrag,
    onMouseLeave,
    onMouseEnter,
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  };
}
